import React, {
  useState,
  useMemo,
  useContext,
  useCallback,
  createContext,
  ReactNode,
  FC,
  useEffect,
} from 'react';

import { GLOBAL_IDS } from '../../utils/constant';

export const defaultGlobalState: GlobalState = {
  organisationName: undefined,
  sortCode: undefined,
  accountNumber: undefined,
  partyId: undefined,
  serviceRequestID: undefined,
  approversID: undefined,
  customerDetails: undefined,
  idvRetryCount: 0,
};

export interface GlobalState {
  organisationName: string;
  sortCode: string;
  accountNumber: string;
  partyId: string;
  serviceRequestID: string;
  approversID: string;
  customerDetails: CustomerDetailsType;
  idvRetryCount: number;
}

interface Individual {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  partyId: string;
  telephone: string;
  email: string;
  title: string;
}

interface Organisation {
  name: string;
  sortCode: string;
  accountNumber: string;
  partyId: string;
}

interface Documents {
  name: string;
  category: string;
  id: string;
}

export interface CustomerDetailsType {
  individual: Individual;
  organisation: Organisation;
  serviceRequestId: string;
  documents: Documents[];
}

interface GlobalContextProps {
  children?: ReactNode;
}

interface GlobalStateContextProps {
  globalState: GlobalState;
  setGlobalContext: (args: any) => void;
}

export const GlobalContext = createContext({} as GlobalStateContextProps);

const fetchInitialState = (): GlobalState => {
  const defaultLocalState = JSON.parse(sessionStorage.getItem(GLOBAL_IDS));

  if (defaultLocalState) {
    return {
      ...defaultLocalState,
    };
  }
  return defaultGlobalState;
};

const GlobalContextProvider: FC<GlobalContextProps> = ({ children }) => {
  const [globalState, setGlobalState] =
    useState<GlobalState>(fetchInitialState);

  const setGlobalContext = useCallback(
    (global: Partial<GlobalState>) => {
      setGlobalState((prevState) => ({
        ...prevState,
        ...global,
      }));
    },
    [setGlobalState],
  );

  const value = useMemo(
    () => ({ globalState, setGlobalContext }),
    [globalState, setGlobalContext],
  );

  useEffect(() => {
    sessionStorage.setItem(GLOBAL_IDS, JSON.stringify(globalState));
  }, [globalState]);

  return (
    <GlobalContext.Provider value={value}>{children}</GlobalContext.Provider>
  );
};

const useGlobalProvider = () => {
  const globalContext = useContext(GlobalContext);

  if (!globalContext) {
    throw new Error('Global config must be used within a provider');
  }

  return globalContext;
};

export { GlobalContextProvider, useGlobalProvider };
